import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useRef, useCallback, useEffect, lazy, Suspense } from "react";
import { motion } from "framer-motion";
import { EscoAttribution } from "../components/DataAttribution";
import { SocialProofBar } from "../components/SocialProofBar";
import { TextRotator } from "../components/landing/TextRotator";
import { LogoCarousel } from "../components/landing/LogoCarousel";
import { BeforeAfter } from "../components/landing/BeforeAfter";
import { SectionHeader } from "../components/landing/SectionHeader";
import { FeatureTabs } from "../components/landing/FeatureTabs";
import { CountUp } from "../components/landing/CountUp";
import { WorkflowDiagram } from "../components/landing/WorkflowDiagram";
import { DataFlowViz } from "../components/landing/DataFlowViz";
import { ComplianceBadges } from "../components/landing/ComplianceBadges";
import { AlchemaLogo } from "../components/AlchemaLogo";
import { DashboardMockup } from "../components/landing/mockups/DashboardMockup";
import { resetCookieConsent } from "../components/CookieConsent";
import { ReduceMotionToggle } from "../components/a11y/ReduceMotionToggle";
import { useMotionPreference } from "~/providers/motion-preference-provider";
import * as m from "~/paraglide/messages";
import {
  landingSchemaGraph,
  serializeJsonLdForHead,
  LINKEDIN_CANONICAL_URL,
} from "~/lib/seo/schema";
import { getLandingFaqContent } from "~/lib/seo/landing-faq";

const SplineBackground = lazy(() => import("../components/SplineBackground"));

const SPLINE_SCENE_URL =
  "https://prod.spline.design/uFubCRYeIr57LrZ6/scene.splinecode?v=4";

export const Route = createFileRoute("/")({
  // Emit one server-authored landing graph in <head>. The default graph call
  // intentionally supplies no AggregateRating until KB-745's source and
  // founder-approval gates are both satisfied.
  head: () => {
    const { faqs } = getLandingFaqContent();

    return {
      scripts: [
        {
          type: "application/ld+json",
          children: serializeJsonLdForHead(landingSchemaGraph(faqs)),
        },
      ],
    };
  },
  component: HomePage,
});

/* ── Viewport threshold for scroll reveals ── */
const REVEAL_VP = { once: true, margin: "-80px" } as const;

function HomePage() {
  const { title: faqTitle, faqs } = getLandingFaqContent();

  // Force scroll to top on mount — overrides TanStack Router's scroll restoration
  // Hide scrollbar on landing page — dark hero clashes with light scrollbar track
  useEffect(() => {
    window.scrollTo(0, 0);
    document.documentElement.classList.add("hide-scrollbar");
    return () => document.documentElement.classList.remove("hide-scrollbar");
  }, []);

  const rotatingWords = [
    m.landing_hero_rotate_resume(),
    m.landing_hero_rotate_cover_letter(),
    m.landing_hero_rotate_interview_prep(),
    m.landing_hero_rotate_salary_pitch(),
  ];

  const featureTabs = [
    { id: "resume", label: m.landing_tab_resume(), description: m.landing_tab_resume_desc() },
    { id: "jobs", label: m.landing_tab_jobs(), description: m.landing_tab_jobs_desc() },
    { id: "keyword", label: m.landing_tab_keyword(), description: m.landing_tab_keyword_desc() },
    { id: "interview", label: m.landing_tab_interview(), description: m.landing_tab_interview_desc() },
    { id: "coverletter", label: m.landing_tab_coverletter(), description: m.landing_tab_coverletter_desc() },
    { id: "salary", label: m.landing_tab_salary(), description: m.landing_tab_salary_desc() },
    { id: "storybank", label: m.landing_tab_storybank(), description: m.landing_tab_storybank_desc() },
    { id: "deepmatch", label: m.landing_tab_deepmatch(), description: m.landing_tab_deepmatch_desc() },
    {
      id: "applicationOverview",
      label: m.landing_application_overview_tab(),
      title: m.landing_application_overview_title(),
      description: m.landing_application_overview_desc(),
    },
    { id: "portal", label: m.landing_tab_portal(), description: m.landing_tab_portal_desc() },
  ];

  const testimonials = [
    {
      quote: m.landing_testimonial_1_quote(),
      name: m.landing_testimonial_1_name(),
      role: m.landing_testimonial_1_role(),
      location: m.landing_testimonial_1_location(),
    },
    {
      quote: m.landing_testimonial_2_quote(),
      name: m.landing_testimonial_2_name(),
      role: m.landing_testimonial_2_role(),
      location: m.landing_testimonial_2_location(),
    },
    {
      quote: m.landing_testimonial_3_quote(),
      name: m.landing_testimonial_3_name(),
      role: m.landing_testimonial_3_role(),
      location: m.landing_testimonial_3_location(),
    },
  ];

  // Double-click prevention for CTA buttons
  const { shouldReduceMotion } = useMotionPreference();
  const [splineReady, setSplineReady] = useState(false);

  const clickGuard = useRef(false);
  const handleCtaClick = useCallback((e: React.MouseEvent) => {
    if (clickGuard.current) {
      e.preventDefault();
      return;
    }
    clickGuard.current = true;
    setTimeout(() => { clickGuard.current = false; }, 1000);
  }, []);


  // Magnetic cursor tracking on hero CTA buttons
  const handleMagnetic = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    if (shouldReduceMotion || !window.matchMedia("(pointer: fine)").matches) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left - rect.width / 2;
    const y = e.clientY - rect.top - rect.height / 2;
    e.currentTarget.style.transform = `translate(${x * 0.15}px, ${y * 0.15}px)`;
  }, [shouldReduceMotion]);

  const handleMagneticReset = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    e.currentTarget.style.transform = "";
  }, []);

  return (
    <div className="flex flex-col" data-homepage-reveal-root>
      {/* ──────────── 1. HERO ──────────── */}

      {/* ── Mobile hero: Attio-style clean light text (no Spline) ── */}
      <div className="sm:hidden" style={{ background: "var(--bg)", width: "100%" }}>
        <div className="flex flex-col items-center px-6 pt-24 pb-16 text-center">
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 16, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={shouldReduceMotion ? { duration: 0.01 } : { type: "spring", stiffness: 100, damping: 22, mass: 0.8 }}
          >
            <span className="hero-badge-light">{m.landing_hero_badge()}</span>
          </motion.div>

          <motion.h1
            data-homepage-hero-heading="mobile"
            className="hero-enter mt-6 font-semibold"
            style={{ fontFamily: "var(--font-display)", lineHeight: 1.1, fontSize: "2.25rem", letterSpacing: "-0.03em", color: "var(--text)", animationDelay: "80ms" }}
            initial={shouldReduceMotion ? false : { opacity: 0, y: 24, filter: "blur(8px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={shouldReduceMotion ? { duration: 0.01 } : {
              type: "spring", stiffness: 90, damping: 20, mass: 0.8, delay: 0.08,
              opacity: { duration: 0.35, ease: [0.16, 1, 0.3, 1], delay: 0.08 },
              filter: { type: "spring", stiffness: 60, damping: 24, mass: 0.8, delay: 0.08 },
            }}
          >
            <span className="block">{m.landing_hero_headline_prefix()}</span>
            <TextRotator words={rotatingWords} className="hero-word-gradient font-semibold" style={{ fontSize: "inherit" }} />
          </motion.h1>

          <motion.p
            data-homepage-hero-description="mobile"
            className="hero-enter mx-auto mt-4 max-w-[36ch] text-sm leading-relaxed"
            style={{ color: "var(--text-secondary)", animationDelay: "180ms" }}
            initial={shouldReduceMotion ? false : { opacity: 0, y: 16, filter: "blur(4px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={shouldReduceMotion ? { duration: 0.01 } : {
              type: "spring", stiffness: 100, damping: 22, mass: 0.8, delay: 0.18,
              opacity: { duration: 0.35, ease: [0.16, 1, 0.3, 1], delay: 0.18 },
              filter: { type: "spring", stiffness: 70, damping: 24, mass: 0.8, delay: 0.18 },
            }}
          >
            {m.landing_hero_subtitle()}
          </motion.p>

          <motion.div
            className="mt-10 flex w-full flex-col items-center gap-4"
            initial={shouldReduceMotion ? false : { opacity: 0, y: 20, filter: "blur(3px)" }}
            animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            transition={shouldReduceMotion ? { duration: 0.01 } : {
              type: "spring", stiffness: 120, damping: 20, mass: 0.7, delay: 0.3,
            }}
          >
            <Link to="/auth/signup" className="mobile-hero-cta" onClick={handleCtaClick} aria-label={m.landing_cta_get_started_aria()}>
              {m.landing_cta_get_started_free()}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M6 3l5 5-5 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>
            </Link>
            <Link to="/auth/login" className="mobile-hero-signin" onClick={handleCtaClick} aria-label={m.landing_cta_sign_in_aria()}>
              {m.auth_sign_in()}
            </Link>
          </motion.div>

          <motion.p
            className="mt-8 text-[0.6875rem] tracking-wide"
            style={{ color: "var(--text-muted)" }}
            initial={shouldReduceMotion ? false : { opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={shouldReduceMotion ? { duration: 0.01 } : { type: "spring", stiffness: 80, damping: 24, mass: 0.8, delay: 0.45 }}
          >
            {m.landing_hero_disclaimer()}
          </motion.p>
        </div>
      </div>

      {/* ── Desktop hero: Spline 3D immersive — spring-orchestrated cascade ── */}
      <div className="hidden sm:block hero-dark-force" style={{ width: "100vw", marginLeft: "calc(-50vw + 50%)", position: "relative", height: "100vh", overflow: "hidden", background: "#0c0c10" }}>
      {/* Background: Spline scene (lazy) — loads behind the text */}
      <div style={{ position: "absolute", inset: 0, zIndex: 0 }}>
        <Suspense fallback={null}>
          <SplineBackground sceneUrl={SPLINE_SCENE_URL} height="100vh" onLoaded={() => setSplineReady(true)} hideObjects={["Text", "a 2", "a_back_light"]} />
        </Suspense>
      </div>

      {/* Aurora placeholder — visible instantly, fades when Spline is ready */}
      <div className={`hero-loading-aurora${splineReady ? " loaded" : ""}`}><div className="aurora-orb" /></div>

      {/* Hero wordmark — unified "Alchem" + cube as one scalable component */}
      <div style={{
        position: "absolute", inset: 0, zIndex: 1,
        display: "flex", alignItems: "center", justifyContent: "center",
        pointerEvents: "none",
      }}>
        <AlchemaLogo darkBg className="hero-wordmark" style={{ fontSize: "clamp(4rem, 10vw, 9rem)", marginTop: "-8vh" }} />
      </div>

      {/* Hero text — pure CSS animations, visible before JS hydrates */}
      <div style={{ position: "relative", zIndex: 2, height: "100%" }}>
        <div className="flex h-full flex-col pb-14">
          <div className="flex-1 min-h-[64%]" />
          <div className="mx-auto w-full max-w-3xl px-8 text-center">
              <div className="hero-enter" style={{ animationDelay: "0ms" }}>
                <span className="hero-badge">{m.landing_hero_badge()}</span>
              </div>

              <h1
                data-homepage-hero-heading="desktop"
                className="mt-5 hero-heading hero-enter font-semibold text-white/90"
                style={{ fontFamily: "var(--font-display)", lineHeight: 1.2, fontSize: "clamp(1.5rem, 3vw + 0.5rem, 2.75rem)", letterSpacing: "-0.03em", animationDelay: "60ms" }}
              >
                <span className="block">{m.landing_hero_headline_prefix()}</span>
                <TextRotator words={rotatingWords} className="hero-word-gradient font-semibold" style={{ fontSize: "inherit" }} />
              </h1>

              <p
                data-homepage-hero-description="desktop"
                className="mx-auto mt-4 max-w-[48ch] text-base leading-relaxed text-white/75 hero-enter"
                style={{ animationDelay: "120ms" }}
              >
                {m.landing_hero_subtitle()}
              </p>

              <div
                className="mt-7 flex flex-col items-center justify-center gap-3 sm:flex-row hero-enter"
                style={{ animationDelay: "180ms" }}
              >
                <Link to="/auth/signup" className="hero-btn-primary" onClick={handleCtaClick} onMouseMove={handleMagnetic} onMouseLeave={handleMagneticReset} aria-label={m.landing_cta_get_started_aria()}>
                  {m.landing_cta_get_started_free()}
                </Link>
                <Link to="/auth/login" className="hero-btn-secondary" onClick={handleCtaClick} onMouseMove={handleMagnetic} onMouseLeave={handleMagneticReset} aria-label={m.landing_cta_sign_in_aria()}>
                  {m.auth_sign_in()}
                </Link>
              </div>

              <p
                className="mt-5 text-[0.6875rem] tracking-wide text-white/60 hero-enter"
                style={{ animationDelay: "240ms" }}
              >
                {m.landing_hero_disclaimer()}
              </p>
          </div>
        </div>
      </div>

      {/* Scroll indicator — luminous thread with chevron */}
      <div className="hero-scroll-indicator" aria-hidden="true">
        <span className="hero-scroll-label">Scroll</span>
        <div className="hero-scroll-chevron" />
      </div>
      </div>

      {/* Dark-to-page gradient transition (desktop only — mobile hero is light) */}
      <div className="hidden sm:block" style={{ height: "80px", background: "linear-gradient(to bottom, #0c0c10, var(--bg))" }} />

      {/* ──────────── LOGO CAROUSEL ──────────── */}
      <LogoCarousel />

      {/* ══════════════════════════════════════════════════
          [01] YOUR AI CAREER ENGINE — Tabbed product showcase
          ══════════════════════════════════════════════════ */}
      <section className="landing-section landing-section--gradient py-16 sm:py-36">
        <div className="mx-auto max-w-6xl px-6">
          <SectionHeader
            number={m.landing_section_01_num()}
            label={m.landing_section_01_label()}
            title={m.landing_section_01_title()}
            description={m.landing_section_01_desc()}
          />
          <div className="mt-14">
            <FeatureTabs tabs={featureTabs} />
          </div>
        </div>

        {/* Inline testimonial — spring + blur */}
        <div className="mx-auto max-w-5xl px-6 mt-20">
          <motion.div
            className="inline-quote"
            initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.96, y: 12, filter: "blur(4px)" }}
            whileInView={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            viewport={REVEAL_VP}
            transition={shouldReduceMotion ? { duration: 0.01 } : {
              type: "spring", stiffness: 100, damping: 22, mass: 0.8,
              filter: { type: "spring", stiffness: 60, damping: 24, mass: 0.8 },
            }}
          >
            <div className="inline-quote-avatar">{testimonials[0].name.charAt(0)}</div>
            <div>
              <blockquote>{testimonials[0].quote}</blockquote>
              <cite>{testimonials[0].name} &middot; {testimonials[0].role}, {testimonials[0].location}</cite>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          [02] BUILT FOR EUROPE — Trust + stats + privacy
          ══════════════════════════════════════════════════ */}
      <section className="landing-section landing-section--alt py-16 sm:py-32">
        <div className="mx-auto max-w-5xl px-6">
          <SectionHeader
            number={m.landing_section_02_num()}
            label={m.landing_section_02_label()}
            title={m.landing_section_02_title()}
            description={m.landing_section_02_desc()}
          />

          {/* Stats counters — spring physics + blur reveal */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-8">
            {[
              { value: 10, label: m.landing_stat_languages_label() },
              { value: 19, label: m.landing_stat_agents_label() },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                className="stat-counter"
                initial={shouldReduceMotion ? false : { opacity: 0, y: 20, filter: "blur(4px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={REVEAL_VP}
                transition={shouldReduceMotion ? { duration: 0.01 } : {
                  type: "spring", stiffness: 110, damping: 20, mass: 0.7, delay: i * 0.08,
                  filter: { type: "spring", stiffness: 70, damping: 24, mass: 0.7, delay: i * 0.08 },
                }}
              >
                <div className="stat-counter-value">
                  <CountUp end={s.value} />
                </div>
                <p className="stat-counter-label">{s.label}</p>
              </motion.div>
            ))}
            {[
              { value: "EU", label: m.landing_stat_hosted_label() },
              { value: "GDPR", label: m.landing_stat_gdpr_label() },
            ].map((s, i) => (
              <motion.div
                key={s.label}
                className="stat-counter"
                initial={shouldReduceMotion ? false : { opacity: 0, y: 20, filter: "blur(4px)" }}
                whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                viewport={REVEAL_VP}
                transition={shouldReduceMotion ? { duration: 0.01 } : {
                  type: "spring", stiffness: 110, damping: 20, mass: 0.7, delay: (i + 2) * 0.08,
                  filter: { type: "spring", stiffness: 70, damping: 24, mass: 0.7, delay: (i + 2) * 0.08 },
                }}
              >
                <div className="stat-counter-value" style={{ fontSize: "clamp(1.25rem, 2.5vw, 2rem)" }}>
                  {s.value}
                </div>
                <p className="stat-counter-label">{s.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Trust row — spring + blur */}
          <motion.div
            className="trust-row mt-12"
            initial={shouldReduceMotion ? false : { opacity: 0, y: 12, filter: "blur(3px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={REVEAL_VP}
            transition={shouldReduceMotion ? { duration: 0.01 } : {
              type: "spring", stiffness: 100, damping: 22, mass: 0.7, delay: 0.3,
            }}
          >
            <div className="trust-row-item">
              <svg className="trust-row-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              <span>{m.landing_trust_gdpr()}</span>
            </div>
            <div className="trust-row-item">
              <svg className="trust-row-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M12 2a4 4 0 014 4v1a1 1 0 001 1h1a4 4 0 010 8h-1a1 1 0 00-1 1v1a4 4 0 01-8 0v-1a1 1 0 00-1-1H6a4 4 0 010-8h1a1 1 0 001-1V6a4 4 0 014-4z" />
                <circle cx="12" cy="12" r="2" />
              </svg>
              <span>{m.landing_trust_ai()}</span>
            </div>
            <div className="trust-row-item">
              <svg className="trust-row-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0110 0v4" />
              </svg>
              <span>{m.landing_trust_nobyok()}</span>
            </div>
          </motion.div>

          <div className="mt-8">
            <SocialProofBar />
          </div>
        </div>

        {/* Inline testimonial — spring + blur */}
        <div className="mx-auto max-w-5xl px-6 mt-20">
          <motion.div
            className="inline-quote"
            initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.96, y: 12, filter: "blur(4px)" }}
            whileInView={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            viewport={REVEAL_VP}
            transition={shouldReduceMotion ? { duration: 0.01 } : {
              type: "spring", stiffness: 100, damping: 22, mass: 0.8,
              filter: { type: "spring", stiffness: 60, damping: 24, mass: 0.8 },
            }}
          >
            <div className="inline-quote-avatar">{testimonials[1].name.charAt(0)}</div>
            <div>
              <blockquote>{testimonials[1].quote}</blockquote>
              <cite>{testimonials[1].name} &middot; {testimonials[1].role}, {testimonials[1].location}</cite>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          [03] FULL PRODUCT SHOWCASE — Dashboard mockup
          ══════════════════════════════════════════════════ */}
      <section className="landing-section py-16 sm:py-36" style={{ backgroundColor: "var(--bg)" }}>
        <div className="mx-auto max-w-6xl px-6">
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 40, filter: "blur(8px)", scale: 0.97 }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)", scale: 1 }}
            viewport={REVEAL_VP}
            transition={shouldReduceMotion ? { duration: 0.01 } : {
              type: "spring", stiffness: 70, damping: 20, mass: 1,
              filter: { type: "spring", stiffness: 40, damping: 22, mass: 1 },
              scale: { type: "spring", stiffness: 90, damping: 22, mass: 0.8 },
            }}
          >
            <DashboardMockup />
          </motion.div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          [05] HOW IT WORKS — Animated workflow pipeline
          ══════════════════════════════════════════════════ */}
      <section className="landing-section landing-section--alt py-16 sm:py-32">
        <div className="mx-auto max-w-5xl px-6">
          <SectionHeader
            number={m.landing_section_05_num()}
            label={m.landing_section_05_label()}
            title={m.landing_section_05_title()}
            description={m.landing_section_05_desc()}
          />
          <WorkflowDiagram />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          [06] CONNECT YOUR SOURCES — Data flow visualization
          ══════════════════════════════════════════════════ */}
      <section className="landing-section py-16 sm:py-32" style={{ backgroundColor: "var(--bg)" }}>
        <div className="mx-auto max-w-5xl px-6">
          <SectionHeader
            number={m.landing_section_06_num()}
            label={m.landing_section_06_label()}
            title={m.landing_section_06_title()}
            description={m.landing_section_06_desc()}
            align="center"
          />
          <DataFlowViz />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          [07] COMPLIANCE — Badges + trust
          ══════════════════════════════════════════════════ */}
      <section className="landing-section landing-section--alt py-16 sm:py-32">
        <div className="mx-auto max-w-5xl px-6">
          <SectionHeader
            number={m.landing_section_07_num()}
            label={m.landing_section_07_label()}
            title={m.landing_section_07_title()}
            description={m.landing_section_07_desc()}
            align="center"
          />
          <ComplianceBadges />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          [09] FAQ — localized catalog content (parity with JSON-LD FAQPage)
          ══════════════════════════════════════════════════ */}
      <section
        className="landing-section py-16 sm:py-32"
        style={{ backgroundColor: "var(--bg)" }}
        aria-labelledby="faq-heading"
      >
        <div className="mx-auto max-w-3xl px-6">
          <h2
            id="faq-heading"
            className="text-center font-semibold"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(1.5rem, 3vw, 2.25rem)",
              letterSpacing: "-0.02em",
              color: "var(--text)",
            }}
          >
            {faqTitle}
          </h2>
          <div className="mt-10 flex flex-col gap-3">
            {faqs.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-xl px-5 py-4"
                style={{
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                }}
              >
                <summary
                  className="cursor-pointer list-none font-medium"
                  style={{ color: "var(--text)" }}
                >
                  {faq.question}
                </summary>
                <p
                  className="mt-3 text-sm leading-relaxed"
                  style={{ color: "var(--text-secondary)" }}
                >
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          [08] SEE THE DIFFERENCE — Before / After
          ══════════════════════════════════════════════════ */}
      <section className="landing-section py-16 sm:py-32" style={{ backgroundColor: "var(--bg)" }}>
        <div className="mx-auto max-w-5xl px-6">
          <SectionHeader
            number={m.landing_section_03_num()}
            label={m.landing_section_03_label()}
            title={m.landing_section_03_title()}
            align="center"
          />
        </div>
        <div className="mt-12">
          <BeforeAfter />
        </div>
      </section>

      {/* ══════════════════════════════════════════════════
          [04] START YOUR JOURNEY — CTA + final testimonial
          ══════════════════════════════════════════════════ */}
      <section className="landing-section landing-section--alt">
        <div className="mx-auto max-w-3xl px-6 cta-section">
          <motion.div
            initial={shouldReduceMotion ? false : { opacity: 0, y: 28, filter: "blur(6px)" }}
            whileInView={{ opacity: 1, y: 0, filter: "blur(0px)" }}
            viewport={REVEAL_VP}
            transition={shouldReduceMotion ? { duration: 0.01 } : {
              type: "spring", stiffness: 80, damping: 20, mass: 0.9,
              filter: { type: "spring", stiffness: 50, damping: 22, mass: 0.9 },
            }}
          >
            <h2 className="cta-heading">{m.landing_section_04_title()}</h2>
            <p className="cta-desc">{m.landing_section_04_desc()}</p>
            <motion.div
              className="mt-10 flex flex-col items-center gap-3 sm:flex-row sm:justify-center"
              initial={shouldReduceMotion ? false : { opacity: 0, y: 12 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={REVEAL_VP}
              transition={shouldReduceMotion ? { duration: 0.01 } : {
                type: "spring", stiffness: 120, damping: 20, mass: 0.7, delay: 0.15,
              }}
            >
              <Link to="/auth/signup" className="btn btn-accent btn-lg" onClick={handleCtaClick} aria-label={m.landing_cta_get_started_aria()}>
                {m.landing_cta_start_free()}
              </Link>
              <Link to="/auth/login" className="btn btn-ghost btn-lg" onClick={handleCtaClick} aria-label={m.landing_cta_sign_in_existing_aria()}>
                {m.landing_cta_sign_in_existing()}
              </Link>
            </motion.div>
          </motion.div>

          {/* Final testimonial — spring + blur */}
          <motion.div
            className="inline-quote mt-16 mx-auto"
            style={{ justifyContent: "center" }}
            initial={shouldReduceMotion ? false : { opacity: 0, scale: 0.96, y: 12, filter: "blur(4px)" }}
            whileInView={{ opacity: 1, scale: 1, y: 0, filter: "blur(0px)" }}
            viewport={REVEAL_VP}
            transition={shouldReduceMotion ? { duration: 0.01 } : {
              type: "spring", stiffness: 100, damping: 22, mass: 0.8, delay: 0.1,
              filter: { type: "spring", stiffness: 60, damping: 24, mass: 0.8, delay: 0.1 },
            }}
          >
            <div className="inline-quote-avatar">{testimonials[2].name.charAt(0)}</div>
            <div>
              <blockquote>{testimonials[2].quote}</blockquote>
              <cite>{testimonials[2].name} &middot; {testimonials[2].role}, {testimonials[2].location}</cite>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ──────────── FOOTER — Attio-exact dark multi-column ──────────── */}
      <footer className="attio-footer">
        {/* SVG 1px separator line — exactly like Attio */}
        <svg width="100%" height="1" className="attio-footer-line"><line x1="0" y1="0.5" x2="100%" y2="0.5" stroke="currentColor" strokeLinecap="round" /></svg>

        {/* Main footer content */}
        <div className="attio-footer-body">
          <div className="attio-footer-grid">
            {/* Col 1: Logo */}
            <div className="attio-footer-logo-col">
              <Link to="/" className="inline-block rounded-lg" aria-label="Alchema home">
                <AlchemaLogo darkBg style={{ fontSize: "1.5rem" }} />
              </Link>
              <p className="attio-footer-tagline">AI-powered career tools.<br />Built in the EU, for the EU.</p>
            </div>

            {/* Col 2+: CSS multi-column link groups — Attio uses columns-4 with break-inside-avoid */}
            <div className="attio-footer-links-col">
              {/* Product */}
              <div className="attio-footer-group">
                <h2 className="attio-footer-heading">Product</h2>
                <ul className="attio-footer-list">
                  <li><Link to="/auth/signup" className="attio-footer-link">{m.common_get_started()}</Link></li>
                  <li><Link to="/pricing" className="attio-footer-link">{m.nav_pricing()}</Link></li>
                </ul>
              </div>

              {/* Company */}
              <div className="attio-footer-group">
                <h2 className="attio-footer-heading">Company</h2>
                <ul className="attio-footer-list">
                  <li><Link to="/impressum" className="attio-footer-link">{m.landing_footer_impressum()}</Link></li>
                  <li><a href="mailto:contact@alchema.eu" className="attio-footer-link">{m.landing_footer_contact()}</a></li>
                </ul>
              </div>

              {/* Legal */}
              <div className="attio-footer-group">
                <h2 className="attio-footer-heading">Legal</h2>
                <ul className="attio-footer-list">
                  <li><Link to="/privacy" className="attio-footer-link">{m.landing_footer_privacy()}</Link></li>
                  <li><Link to="/agb" className="attio-footer-link">{m.landing_footer_terms()}</Link></li>
                  <li><Link to="/widerruf" className="attio-footer-link">{m.consumer_declaration_withdraw_first()}</Link></li>
                  <li><Link to="/kuendigung" className="attio-footer-link">{m.consumer_declaration_termination_first()}</Link></li>
                  <li><Link to="/barrierefreiheit" className="attio-footer-link">{m.landing_accessibility_link()}</Link></li>
                  <li>
                    <button type="button" onClick={resetCookieConsent} className="attio-footer-link attio-footer-btn">
                      {m.landing_cookie_settings()}
                    </button>
                  </li>
                </ul>
              </div>

              {/* Resources */}
              <div className="attio-footer-group">
                <h2 className="attio-footer-heading">Resources</h2>
                <ul className="attio-footer-list">
                  <li><EscoAttribution /></li>
                  <li><ReduceMotionToggle compact /></li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar — darker bg strip with social / copyright / legal */}
        <div className="attio-footer-bottom">
          <div className="attio-footer-bottom-inner">
            {/* Social icons */}
            <div className="attio-footer-social">
              <a href={LINKEDIN_CANONICAL_URL} target="_blank" rel="noopener noreferrer" className="attio-footer-social-icon" aria-label="Alchema on LinkedIn">
                <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 448 512" height="1em" width="1em"><path d="M100.28 448H7.4V148.9h92.88zM53.79 108.1C24.09 108.1 0 83.5 0 53.8a53.79 53.79 0 0 1 107.58 0c0 29.7-24.1 54.3-53.79 54.3zM447.9 448h-92.68V302.4c0-34.7-.7-79.2-48.29-79.2-48.29 0-55.69 37.7-55.69 76.7V448h-92.78V148.9h89.08v40.8h1.3c12.4-23.5 42.69-48.3 87.88-48.3 94 0 111.28 61.9 111.28 142.3V448z" /></svg>
              </a>
            </div>

            {/* Copyright */}
            <span className="attio-footer-copy">&copy; {new Date().getFullYear()} {m.landing_footer_copyright()}. All rights reserved.</span>

            {/* Legal links */}
            <div className="attio-footer-legal">
              <Link to="/privacy" className="attio-footer-legal-link">{m.landing_footer_privacy()}</Link>
              <Link to="/agb" className="attio-footer-legal-link">{m.landing_footer_terms()}</Link>
              <Link to="/widerruf" className="attio-footer-legal-link">{m.consumer_declaration_withdraw_first()}</Link>
              <Link to="/kuendigung" className="attio-footer-legal-link">{m.consumer_declaration_termination_first()}</Link>
              <Link to="/impressum" className="attio-footer-legal-link">{m.landing_footer_impressum()}</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
